/**
 * Suggest a line's Tech 1-4 from the job crew's positions (API 0425 / 0426).
 *
 * A position lists the line item types a hand in it works — Tong Hand →
 * power_tongs. A hand holds a position ON THE JOB (`positions` on each crew
 * row). So when a line's type is known and nobody has been put on it, the crew
 * whose job position lists that type are the obvious answer, and this fills
 * them in, in crew order, slot 1 first. Slot 1 is the line's Lead
 * (role_on_ticket, derived server-side from assigned_tech_1_id).
 *
 * IT ONLY SUGGESTS. The rules, each of which is a guard below:
 *
 *   - A line with ANY slot set is returned untouched. Somebody chose that
 *     crew; a suggestion never overwrites a choice. That includes an AI-drafted
 *     line, whose slots are the draft's answer.
 *   - Only crew whose JOB position lists the type. Never the hand's primary
 *     position (`position_name`) — that is what they usually do, not what they
 *     are doing on this job — and never a guess from a name or role.
 *   - No match leaves the slots empty. Crew size on a line is "hands that
 *     worked the line"; filling a slot with someone who merely exists on the
 *     roster would move the crew-size tier and real money.
 *   - At most four, because there are four slots. A fifth match is still in
 *     the picker; it just is not suggested.
 *
 * `phase` scopes to one phase's positions (plus whole-job positions, which
 * cover every phase). Omitted, any phase counts. TICKETS RECORD NO PHASE TODAY
 * (no column on `tickets`, checked against schema.ts 2026-10-01), so both
 * editors call this without one — the parameter is here so the day a ticket
 * carries its phase, a Tong Hand on the run only stops being suggested for the
 * wash ticket by passing one argument.
 *
 * Every slot is written through `assignTechSlot`, the same writer the pickers
 * use, so the initials can never drift from the ids.
 */
import { readJobPositions, type JobPositionPhase } from './job-crew-positions';
import {
  assignTechSlot,
  crewId,
  TECH_SLOTS,
  type CrewMemberLike,
  type TechSlotFields,
} from './crew-slot';

export interface PrefillableLine extends TechSlotFields {
  lineItemType: string;
}

export function anyTechSlotSet(line: TechSlotFields): boolean {
  return TECH_SLOTS.some(s => !!line[`assignedTech${s}Id` as const] || !!line[`assignedTech${s}Initials` as const]);
}

/** Empty all four slots — for taking back a suggestion nobody has touched. */
export function clearTechSlots<T extends TechSlotFields>(line: T): T {
  return TECH_SLOTS.reduce((acc, s) => assignTechSlot(acc, s, null, undefined), line);
}

/** The crew whose job position (in `phase`, if given) lists `lineItemType`, crew order, deduplicated. */
export function crewForLineItemType(
  crew: readonly CrewMemberLike[],
  lineItemType: string,
  phase?: JobPositionPhase,
): CrewMemberLike[] {
  if (!lineItemType) return [];
  const seen = new Set<string>();
  const out: CrewMemberLike[] = [];
  for (const member of crew) {
    const id = member ? crewId(member) : '';
    if (!id || seen.has(id)) continue;
    const works = readJobPositions(member.positions).some(p =>
      (phase === undefined || p.phase === null || p.phase === phase) && p.lineItemTypes.includes(lineItemType),
    );
    if (!works) continue;
    seen.add(id);
    out.push(member);
  }
  return out;
}

/**
 * The line with its empty slots suggested from the crew. `prefilled` is true
 * only when at least one slot was filled — the editor marks the line with it.
 * The line object is returned as-is whenever nothing changes.
 */
export function prefillTechSlots<T extends PrefillableLine>(
  line: T,
  crew: readonly CrewMemberLike[],
  phase?: JobPositionPhase,
): { line: T; prefilled: boolean } {
  if (anyTechSlotSet(line)) return { line, prefilled: false };
  const matches = crewForLineItemType(crew, line.lineItemType, phase).slice(0, TECH_SLOTS.length);
  if (matches.length === 0) return { line, prefilled: false };
  const filled = matches.reduce((acc, member, i) => assignTechSlot(acc, TECH_SLOTS[i], crewId(member), member), line);
  return { line: filled, prefilled: true };
}
